import { useCallback, useEffect, useState } from "react";
import {
  BackupReportItem,
  fetchBackupDownloadHistory,
  fetchBackupHistory,
  fetchBackupReport,
} from "../../../api/admin";
import { UserListContainer } from "../../../style/contents/backup/backup";
import ContentsTopHeader from "../../../components/common/contents.top.header";
import HistoryList from "../../../components/history/history.list";
import HistoryReport from "../../../components/history/history.report";
import HistoryTabs from "../../../components/history/history.tab.menu";

export interface BackupDownloadHistory {
  backupZipName: string; // 백업 zip 파일 이름
  createdDate: string; // 생성일 (yyyy-MM-dd)
  ipAddress: string; // 다운로드 요청한 IP
  loginTime: string; // 로그인 시간 (ISO 8601)
  userAccount: string; // 사용자 계정 ID
  userName: string; // 사용자 이름
  deviceId: string;
}

type BackupDownloadHistoryListType = BackupDownloadHistory[];

export type HistoryCategory = "BACKUP" | "DOWNLOAD";

export interface UnifiedHistoryRow {
  category: HistoryCategory; // ⭐ 구분 기준
  name: string; // 백업명 or zip명
  userAccount: string;
  userName: string;
  deviceId: string;
  ipAddress: string;
  createdDate: string;
  loginTime: string;
  type: "file" | "db" | "image" | "-";
  state: string | "-";
}

const mapDownloadHistory = (item: any): UnifiedHistoryRow => ({
  category: "DOWNLOAD",
  name: item.name,
  userAccount: item.userAccount,
  userName: item.userName,
  deviceId: item.deviceId,
  ipAddress: item.ipAddress,
  createdDate: item.createdDate,
  loginTime: item.loginTime,
  type: "-", // ❗ 다운로드에는 없음
  state: "-", // ❗ 다운로드에는 없음
});

const mapBackupHistory = (item: any): UnifiedHistoryRow => ({
  category: "BACKUP",
  name: item.name,
  userAccount: item.userAccount,
  userName: item.userName,
  deviceId: item.deviceId,
  ipAddress: item.ipAddress,
  createdDate: item.createdDate,
  loginTime: item.loginTime,
  type: item.type ?? "-",
  state: item.state ?? "-",
});

const formatDateParam = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

export type ReportRangeType = "7" | "15" | "30" | "custom";

export interface ReportDateRange {
  startDate: string;
  endDate: string;
}

const getRecentDateRange = (days: number): ReportDateRange => {
  const endDate = new Date();
  const startDate = new Date(endDate);
  startDate.setDate(endDate.getDate() - (days - 1));

  return {
    startDate: formatDateParam(startDate),
    endDate: formatDateParam(endDate),
  };
};

type HistoryMainTab = "HISTORY" | "REPORT";

const BackupHistory = () => {
  const [searchKeyword, setSearchKeyword] = useState("");
  const [pageSize, setPageSize] = useState("10");

  const [data, setData] = useState<UnifiedHistoryRow[]>([]);
  const [reportData, setReportData] = useState<BackupReportItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reportError, setReportError] = useState<string | null>(null);
  const [reportRangeType, setReportRangeType] =
    useState<ReportRangeType>("30");
  const [reportDateRange, setReportDateRange] = useState<ReportDateRange>(() =>
    getRecentDateRange(30),
  );

  const [activeTab, setActiveTab] = useState<HistoryMainTab>("HISTORY");

  const adminUserId = localStorage.getItem("userId") ?? ""; // ✅ 관리자 ID 가져오기
  const companyCode = localStorage.getItem("companyCode") ?? "";

  console.log(data);
  // name 이 경로

  const handleSearch = (keyword: string) => {
    setSearchKeyword(keyword);
    // TODO: fetchData(keyword, pageSize);
  };

  const handlePageSizeChange = (size: string) => {
    setPageSize(size);
    // TODO: fetchData(searchKeyword, size);
  };

  // const loadHistoryList = useCallback(async () => {
  //   setLoading(true);
  //   try {
  //     const res = await fetchBackupDownloadHistory(adminUserId);
  //     console.log("✅ 다운로드 이력:", res);
  //     setData(res.backupZipHistoryList || []);
  //     // ⚠️ 백엔드 응답 구조에 맞춰서 수정 필요
  //   } catch (err: any) {
  //     console.error("❌ 다운로드 이력 불러오기 실패:", err);
  //     setError("다운로드 이력을 불러오는 데 실패했습니다.");
  //   } finally {
  //     setLoading(false);
  //   }
  // }, []);

  const loadHistoryList = useCallback(async () => {
    setLoading(true);
    try {
      const [downloadRes, backupRes] = await Promise.all([
        fetchBackupDownloadHistory(adminUserId),
        fetchBackupHistory(adminUserId),
      ]);

      const downloadRows: UnifiedHistoryRow[] = (
        downloadRes.backupZipHistoryList || []
      ).map(mapDownloadHistory);

      const backupRows: UnifiedHistoryRow[] = (
        backupRes.backupProgressHistoryList || []
      ).map(mapBackupHistory);

      const merged = [...backupRows, ...downloadRows].sort(
        (a, b) =>
          new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
      );

      setData(merged);
    } catch (err) {
      console.error(err);
      setError("이력을 불러오는 데 실패했습니다.");
    } finally {
      setLoading(false);
    }
  }, [adminUserId]);

  const loadReportList = useCallback(async () => {
    if (
      !adminUserId ||
      !companyCode ||
      !reportDateRange.startDate ||
      !reportDateRange.endDate
    ) {
      return;
    }

    setReportLoading(true);
    setReportError(null);

    try {
      const response = await fetchBackupReport(adminUserId, {
        companyCode,
        startDate: reportDateRange.startDate,
        endDate: reportDateRange.endDate,
      });

      setReportData(response.rawData || []);
    } catch (err) {
      console.error(err);
      setReportError("리포트를 불러오는 데 실패했습니다.");
    } finally {
      setReportLoading(false);
    }
  }, [adminUserId, companyCode, reportDateRange]);

  const handleReportRangeSelect = (rangeType: Exclude<ReportRangeType, "custom">) => {
    setReportRangeType(rangeType);
    setReportDateRange(getRecentDateRange(Number(rangeType)));
  };

  const handleReportCustomDateChange = (
    field: keyof ReportDateRange,
    value: string,
  ) => {
    setReportRangeType("custom");
    setReportDateRange((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  useEffect(() => {
    loadHistoryList();
  }, [loadHistoryList]);

  useEffect(() => {
    loadReportList();
  }, [loadReportList]);

  const normalizedKeyword = searchKeyword.trim().toLowerCase();
  const filteredData = data.filter((user) => {
    const keyword = normalizedKeyword;

    return (
      user.userAccount.toLowerCase().includes(keyword) ||
      user.userName.toLowerCase().includes(keyword) ||
      user.name?.toLowerCase().includes(keyword) ||
      user.createdDate?.toLowerCase().includes(keyword) ||
      user.loginTime?.toLowerCase().includes(keyword) ||
      user.ipAddress?.toLowerCase().includes(keyword) ||
      user.type?.toLowerCase().includes(keyword)
    );
  });

  const filteredReportData = reportData.filter((item) => {
    const keyword = normalizedKeyword;

    return (
      item.type?.toLowerCase().includes(keyword) ||
      item.userAccount?.toLowerCase().includes(keyword) ||
      item.targetPath?.toLowerCase().includes(keyword) ||
      item.deviceId?.toLowerCase().includes(keyword) ||
      item.date?.toLowerCase().includes(keyword) ||
      item.remoteIpAddress?.toLowerCase().includes(keyword) ||
      item.state?.toLowerCase().includes(keyword)
    );
  });

  if (loading) return <div>⏳ 이력을 불러오는 중...</div>;
  if (error) return <div>{error}</div>;

  return (
    <UserListContainer>
      {" "}
      <ContentsTopHeader
        titleSlot={
          <HistoryTabs activeTab={activeTab} onChange={setActiveTab} />
        }
        onSearch={handleSearch}
        onPageSizeChange={handlePageSizeChange}
        className="user-list-top-header"
      />
      <div className="contents-body">
        {activeTab === "HISTORY" && (
          <HistoryList
            data={filteredData}
            pageSize={parseInt(pageSize)}
            onUpdateSuccess={loadHistoryList}
            isLoading={loading}
          />
        )}

        {activeTab === "REPORT" && (
          <>
            {reportError ? (
              <div>{reportError}</div>
            ) : (
              <HistoryReport
                data={filteredReportData}
                pageSize={parseInt(pageSize)}
                isLoading={reportLoading}
                rangeType={reportRangeType}
                dateRange={reportDateRange}
                onRangeSelect={handleReportRangeSelect}
                onCustomDateChange={handleReportCustomDateChange}
              />
            )}
          </>
        )}
      </div>
    </UserListContainer>
  );
};

export default BackupHistory;
